/**
 * «یار» assistant characters (design: Sakhtar Assistant Characters v1.0).
 * One body, one colour pair per module. The FAB shows ONLY the character of the
 * module the user is in; outside every module the general «ساختار یار» is shown.
 */
import type { AiDomain, AiTool } from '../../routes/ai/connect/contract.ts';

export type YarKey = 'sakhtar' | 'sales' | 'finance' | 'inventory' | 'procurement' | 'projects';

export interface YarDef {
  readonly key: YarKey;
  readonly name: string;
  /** Path segment under `/t/:slug` the character belongs to; `null` = general. */
  readonly moduleId: string | null;
  readonly moduleLabel: string;
  readonly image: string;
  readonly main: string;
  readonly light: string;
}

const img = (key: YarKey) => `/assistants/yar-${key}.png`;

export const YARS: Readonly<Record<YarKey, YarDef>> = {
  sakhtar: {
    key: 'sakhtar',
    name: 'ساختار یار',
    moduleId: null,
    moduleLabel: 'همهٔ ماژول‌ها',
    image: img('sakhtar'),
    main: '#17243d',
    light: '#ecf0f8',
  },
  sales: {
    key: 'sales',
    name: 'فروش یار',
    moduleId: 'crm',
    moduleLabel: 'فروش',
    image: img('sales'),
    main: '#3461c9',
    light: '#e9f0ff',
  },
  finance: {
    key: 'finance',
    name: 'مالی یار',
    moduleId: 'finance',
    moduleLabel: 'مالی و حسابداری',
    image: img('finance'),
    main: '#117b5a',
    light: '#dcf8ea',
  },
  inventory: {
    key: 'inventory',
    name: 'انبار یار',
    moduleId: 'inventory',
    moduleLabel: 'انبار',
    image: img('inventory'),
    main: '#995600',
    light: '#ffecdd',
  },
  procurement: {
    key: 'procurement',
    name: 'تدارکات یار',
    moduleId: 'procurement',
    moduleLabel: 'خرید و تأمین',
    image: img('procurement'),
    main: '#7b47bf',
    light: '#f2edff',
  },
  projects: {
    key: 'projects',
    name: 'پروژه یار',
    moduleId: 'projects',
    moduleLabel: 'پروژه‌ها و کارها',
    image: img('projects'),
    main: '#09787c',
    light: '#d6f8f9',
  },
};

export function yarForModule(moduleId: string): YarDef | null {
  return Object.values(YARS).find((y) => y.moduleId === moduleId) ?? null;
}

export interface YarPlacement {
  readonly yar: YarDef;
  /** Where the FAB leads: the module's own assistant tab, or the general assistant. */
  readonly href: string;
}

/**
 * The FAB for a route, or `null` when the user is already on the assistant
 * page itself (the module's «دستیار» tab or `/ai`). `base` is `/t/{slug}`.
 */
export function yarPlacementForPath(pathname: string, base: string): YarPlacement | null {
  const rest = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  const parts = rest.split('/').filter(Boolean);
  const first = parts[0] ?? '';
  if (first === 'ai') return null;
  const own = first === '' ? null : yarForModule(first);
  if (own) {
    if (parts[1] === 'assistant') return null;
    return { yar: own, href: `${base}/${first}/assistant` };
  }
  return { yar: YARS.sakhtar, href: `${base}/ai` };
}

/** The assistant answers only for paying tenants; trial and demo show the interface only. */
export function isAssistantActive(input: {
  readonly slug: string;
  readonly subscriptionMode: 'trial' | 'paid' | null;
}): boolean {
  if (input.slug === 'demo' || input.slug === 'demo-preview') return false;
  return input.subscriptionMode === 'paid';
}

export interface ModuleYarWiring {
  readonly assistantKey: YarKey;
  readonly tool: AiTool;
  readonly domain: AiDomain;
  /** Ready questions the tool's data can answer. They only fill the composer. */
  readonly suggestions: readonly string[];
}

export const MODULE_YAR_MAP: Readonly<Record<string, ModuleYarWiring>> = {
  inventory: {
    assistantKey: 'inventory',
    tool: 'inventory.stock.summary',
    domain: 'inventory',
    suggestions: [
      'موجودی هر انبار چقدر است؟',
      'کدام کالاها موجودی کمی دارند؟',
      'بیشترین موجودی مربوط به کدام کالاست؟',
    ],
  },
  crm: {
    assistantKey: 'sales',
    tool: 'crm.opportunity.open',
    domain: 'sales',
    suggestions: [
      'فرصت‌های فروش باز کدام‌اند؟',
      'بزرگ‌ترین فرصت‌های در جریان کدام‌اند؟',
      'کدام فرصت‌ها نزدیک تاریخ بستن هستند؟',
    ],
  },
  procurement: {
    assistantKey: 'procurement',
    tool: 'procurement.order.open',
    domain: 'procurement',
    suggestions: [
      'سفارش‌های خرید باز کدام‌اند؟',
      'کدام سفارش‌ها هنوز کامل دریافت نشده‌اند؟',
      'بیشترین مبلغ خرید باز مربوط به کدام تأمین‌کننده است؟',
    ],
  },
  finance: {
    assistantKey: 'finance',
    tool: 'accounting.open-items',
    domain: 'finance',
    suggestions: [
      'مطالبات سررسیدگذشته کدام‌اند؟',
      'کدام بدهی‌ها نزدیک سررسید هستند؟',
      'جمع مطالبات و بدهی‌های باز چقدر است؟',
    ],
  },
  projects: {
    assistantKey: 'projects',
    tool: 'projects.task.overdue',
    domain: 'projects',
    suggestions: [
      'کدام کارها عقب افتاده‌اند؟',
      'بیشترین تأخیر مربوط به کدام کار است؟',
      'کارهای عقب‌افتادهٔ هر پروژه چندتاست؟',
    ],
  },
};
